const { SessionJsonStorage } = require('./storage');

export class SelectionManager {
  constructor(storageKey) {
    this.storageKey = storageKey;
  }

  // Get selection state
  getSelection() {
    return SessionJsonStorage.get(this.storageKey) || {
      selectAll: false,
      excludedIds: [],
      includedIds: [],
    };
  }

  // Update selection state
  updateSelection(payload) {
    SessionJsonStorage.set({
      key: this.storageKey,
      value: {
        ...this.getSelection(),
        ...payload,
      },
    });
  }

  // Clear all selections
  clearSelection() {
    SessionJsonStorage.remove(this.storageKey);
  }

  // Set select all mode
  setSelectAll(selectAll) {
    if (selectAll) {
      // When enabling select all, clear included and start tracking excluded
      this.updateSelection({
        selectAll: true,
        excludedIds: [],
        includedIds: [],
      });
    } else {
      // When disabling select all, clear excluded and start tracking included
      this.updateSelection({
        selectAll: false,
        excludedIds: [],
        includedIds: [],
      });
    }
  }

  // Check if an item is selected
  isSelected(itemId) {
    const { selectAll, excludedIds, includedIds } = this.getSelection();

    if (selectAll) {
      // In select all mode, item is selected unless it's excluded
      return !excludedIds.includes(itemId);
    }
    // In individual selection mode, item is selected only if included
    return includedIds.includes(itemId);
  }

  // Toggle selection for an item
  toggleSelection(itemId) {
    const { selectAll, excludedIds, includedIds } = this.getSelection();

    if (selectAll) {
      // In select all mode, toggle means add/remove from excluded
      if (excludedIds.includes(itemId)) {
        this.updateSelection({
          excludedIds: excludedIds.filter((id) => id !== itemId),
        });
      } else {
        this.updateSelection({
          excludedIds: [...excludedIds, itemId],
        });
      }
      return;
    }

    // In individual selection mode, toggle means add/remove from included
    if (includedIds.includes(itemId)) {
      this.updateSelection({
        includedIds: includedIds.filter((id) => id !== itemId),
      });
    } else {
      this.updateSelection({
        includedIds: [...includedIds, itemId],
      });
    }
  }

  // Get selected IDs (returns object with selectAll flag and relevant IDs)
  getSelectedIds() {
    const { selectAll, excludedIds, includedIds } = this.getSelection();

    if (selectAll) {
      // In select all mode, return excluded IDs
      return { selectAll: true, excludedIds };
    }
    // In individual selection mode, return included IDs
    return { selectAll: false, includedIds };
  }

  // Get selected count based on total count
  getSelectedCount(totalCount) {
    const { selectAll, excludedIds, includedIds } = this.getSelection();

    if (selectAll) {
      return Math.max(0, totalCount - excludedIds.length);
    }
    return includedIds.length;
  }

  // Get selected item IDs for current page
  getPageSelectedIds(pageItems) {
    return pageItems.filter((item) => this.isSelected(item.id)).map((item) => item.id);
  }

  // Check if all items on current page are selected
  isPageFullySelected(pageItems) {
    return pageItems.every((item) => this.isSelected(item.id));
  }

  // Check if some items on current page are selected
  isPagePartiallySelected(pageItems) {
    const selectedCount = pageItems.filter((item) => this.isSelected(item.id)).length;
    return selectedCount > 0 && selectedCount < pageItems.length;
  }
}
